import { useEffect, useRef, useState } from "react";
import Plot from "react-plotly.js";
import useProjectStore from "../store/projectStore";
import { geometryApi } from "../api/blades";
import { bladeMesh, revolutionMesh } from "../utils/bladeMesh";

import { useChartColors, getChartColors } from "../chartTheme";
const DEG2RAD = Math.PI / 180;

const MODES = [
  { key: "hs",   label: "H-S"       },
  { key: "wrap", label: "Wrap"      },
  { key: "conf", label: "Conf. Map" },
  { key: "cone", label: "Cone Area" },
  { key: "porc", label: "Porc"      },
  { key: "curv", label: "Curv"      },
  { key: "skel", label: "Skel"      },
  { key: "3d",   label: "3D"        },
];

const CURV_SCALE = 30;
const CURV_W     = 12;

function buildPorcupineTraces(processed, colorByName) {
  const { LINE_COLORS } = getChartColors();
  const hubLine    = processed.find(l => l.name.toUpperCase().includes("HUB"));
  const shroudLine = processed.find(l => l.name.toUpperCase().includes("SHROUD"));
  const targets    = [hubLine, shroudLine].filter(Boolean);
  if (targets.length === 0) return [];

  function curvOf(r, z) {
    const n = r.length;
    if (n < 4) return null;
    let totalS = 0;
    for (let i = 1; i < n; i++) {
      const dr = r[i] - r[i-1], dz = z[i] - z[i-1];
      totalS += Math.sqrt(dr*dr + dz*dz);
    }
    const h = totalS / (n - 1);
    const kappa = new Float64Array(n);
    const nr    = new Float64Array(n);
    const nz    = new Float64Array(n);
    for (let i = 1; i < n - 1; i++) {
      const drds  = (r[i+1] - r[i-1]) / (2*h);
      const dzds  = (z[i+1] - z[i-1]) / (2*h);
      const d2rds = (r[i+1] - 2*r[i] + r[i-1]) / (h*h);
      const d2zds = (z[i+1] - 2*z[i] + z[i-1]) / (h*h);
      kappa[i] = drds * d2zds - dzds * d2rds;
      nr[i]    = -dzds;
      nz[i]    =  drds;
    }
    kappa[0] = kappa[1];     nr[0] = nr[1];     nz[0] = nz[1];
    kappa[n-1] = kappa[n-2]; nr[n-1] = nr[n-2]; nz[n-1] = nz[n-2];
    // Moving-average smoothing
    const smoothed = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      let sum = 0, cnt = 0;
      for (let j = Math.max(0, i - CURV_W); j <= Math.min(n - 1, i + CURV_W); j++) {
        sum += kappa[j]; cnt++;
      }
      smoothed[i] = sum / cnt;
    }
    return { kappa: smoothed, nr, nz };
  }

  const curves = targets.map(l => ({ line: l, c: curvOf(l.r, l.z) })).filter(x => x.c);
  const maxK   = Math.max(...curves.flatMap(({ c }) => Array.from(c.kappa).map(Math.abs)));
  if (maxK < 1e-12) return [];

  const baseTraces = targets.map(l => ({
    type: "scatter", mode: "lines", name: l.name,
    x: l.r, y: l.z,
    line: { color: colorByName[l.name] || LINE_COLORS[0], width: 2 },
  }));

  const porcTraces = curves.flatMap(({ line, c }) => {
    const { r, z } = line;
    const rSegs = [], zSegs = [];
    for (let i = 0; i < r.length; i++) {
      const kn = c.kappa[i] / maxK;
      rSegs.push(r[i], r[i] + CURV_SCALE * kn * c.nr[i], null);
      zSegs.push(z[i], z[i] + CURV_SCALE * kn * c.nz[i], null);
    }
    return [{
      type: "scatter", mode: "lines", name: `${line.name} κ`,
      x: rSegs, y: zSegs,
      line: { color: colorByName[line.name] || LINE_COLORS[0], width: 0.8 },
      hoverinfo: "skip",
    }];
  });

  return [...baseTraces, ...porcTraces];
}

function inflectionMarker(x, y, z) {
  return {
    type: "scatter3d", x, y, z,
    mode: "markers",
    marker: { size: 2.5, color: "#111827", opacity: 0.85 },
    hoverinfo: "skip",
    showlegend: false,
  };
}

function computeInflection(data, hasSurface) {
  const nv = data.kappa_v.length;
  const nu = data.kappa_v[0]?.length ?? 0;
  const curvX = [], curvY = [], curvZ = [];
  const skelX = [], skelY = [], skelZ = [];
  for (let iv = 0; iv < nv; iv++) {
    for (let iu = 0; iu < nu - 1; iu++) {
      const k0 = data.kappa_v[iv][iu], k1 = data.kappa_v[iv][iu + 1];
      if (k0 * k1 < 0) {
        const t = k0 / (k0 - k1);
        curvX.push(data.u[iu] + t * (data.u[iu + 1] - data.u[iu]));
        curvY.push(data.v[iv]);
        curvZ.push(0);
        if (hasSurface) {
          skelX.push(data.surface_x[iv][iu] + t * (data.surface_x[iv][iu + 1] - data.surface_x[iv][iu]));
          skelY.push(data.surface_y[iv][iu] + t * (data.surface_y[iv][iu + 1] - data.surface_y[iv][iu]));
          skelZ.push(data.surface_z[iv][iu] + t * (data.surface_z[iv][iu + 1] - data.surface_z[iv][iu]));
        }
      }
    }
  }
  for (let iv = 0; iv < nv - 1; iv++) {
    for (let iu = 0; iu < nu; iu++) {
      const k0 = data.kappa_v[iv][iu], k1 = data.kappa_v[iv + 1][iu];
      if (k0 * k1 < 0) {
        const t = k0 / (k0 - k1);
        curvX.push(data.u[iu]);
        curvY.push(data.v[iv] + t * (data.v[iv + 1] - data.v[iv]));
        curvZ.push(0);
        if (hasSurface) {
          skelX.push(data.surface_x[iv][iu] + t * (data.surface_x[iv + 1][iu] - data.surface_x[iv][iu]));
          skelY.push(data.surface_y[iv][iu] + t * (data.surface_y[iv + 1][iu] - data.surface_y[iv][iu]));
          skelZ.push(data.surface_z[iv][iu] + t * (data.surface_z[iv + 1][iu] - data.surface_z[iv][iu]));
        }
      }
    }
  }
  return { curvX, curvY, curvZ, skelX, skelY, skelZ };
}

export default function LivePreviewPanel() {
  const { LINE_COLORS, COLOR_PRIMARY, COLOR_DARK, COLOR_LIGHT } = useChartColors();
  const { activeDesign } = useProjectStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("hs");

  // Curvature data — fetched lazily on first "curv" or "skel" selection
  const [curvData, setCurvData] = useState(null);
  const [curvLoading, setCurvLoading] = useState(false);

  const containerRef  = useRef(null);
  const cameraRef     = useRef({ eye: { x: 1.5, y: 1.5, z: 0.8 } });
  const curvCamRef    = useRef({ eye: { x: 0, y: -2.0, z: 1.2 } });
  const skelCamRef    = useRef({ eye: { x: 1.5, y: 1.5, z: 0.8 } });
  const axisRangesRef = useRef({});

  // ResizeObserver so Plotly reacts to panel-divider drags.
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => window.dispatchEvent(new Event("resize")));
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Blade views — fetched on every design change.
  useEffect(() => {
    if (!activeDesign?.id) return;
    setLoading(true);
    geometryApi.bladeViews(activeDesign.id)
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDesign]);

  // Reset curvature data and axis ranges when design changes.
  useEffect(() => {
    axisRangesRef.current = {};
    setCurvData(null);
  }, [activeDesign?.id]);

  // Curvature data — lazy fetch when curv or skel mode is first opened.
  useEffect(() => {
    if (!activeDesign?.id) return;
    if (mode !== "curv" && mode !== "skel") return;
    if (curvData || curvLoading) return;
    setCurvLoading(true);
    geometryApi.curvatureMap(activeDesign.id)
      .then(({ data }) => setCurvData(data))
      .catch(() => {})
      .finally(() => setCurvLoading(false));
  }, [mode, activeDesign?.id, curvData, curvLoading]);

  const onRelayout2D = (key) => (e) => {
    const r = { ...axisRangesRef.current[key] };
    if ("xaxis.range[0]" in e) r.x = [e["xaxis.range[0]"], e["xaxis.range[1]"]];
    if ("yaxis.range[0]" in e) r.y = [e["yaxis.range[0]"], e["yaxis.range[1]"]];
    if (e["xaxis.autorange"]) r.x = null;
    if (e["yaxis.autorange"]) r.y = null;
    axisRangesRef.current[key] = r;
  };

  const savedRange = (key) => {
    const r = axisRangesRef.current[key] || {};
    return { x: r.x ? { range: r.x } : {}, y: r.y ? { range: r.y } : {} };
  };

  // Shared color lookup keyed by name.
  const designCls = activeDesign?.construction_lines || [];
  const colorByName = {};
  designCls.forEach((cl, i) => {
    colorByName[cl.name] = LINE_COLORS[i % LINE_COLORS.length];
  });
  const processed = data?.processed_lines || [];
  const hasProcessed = processed.length > 0;

  // ── Hub-Shroud traces ──────────────────────────────────────────────────────
  const hsTraces = [
    ...(data?.hub_shroud || []).map((l) => ({
      type: "scatter", mode: "lines",
      name: hasProcessed ? `${l.name} (MECO)` : l.name,
      x: l.r, y: l.z,
      line: {
        color: hasProcessed ? (colorByName[l.name] || LINE_COLORS[0]) + "40" : (colorByName[l.name] || LINE_COLORS[0]),
        width: hasProcessed ? 1 : 1.8,
        dash: hasProcessed ? "dot" : "solid",
      },
      showlegend: !hasProcessed,
    })),
    ...processed.map(l => ({
      type: "scatter", mode: "lines", name: l.name,
      x: l.r, y: l.z,
      line: { color: colorByName[l.name] || LINE_COLORS[0], width: 2 },
    })),
    ...(hasProcessed ? [
      {
        type: "scatter", mode: "lines", name: "LE edge", showlegend: false,
        x: processed.map(l => l.le_r), y: processed.map(l => l.le_z),
        line: { color: "#111827", width: 1 },
        hoverinfo: "skip",
      },
      {
        type: "scatter", mode: "lines", name: "TE edge", showlegend: false,
        x: processed.map(l => l.te_r), y: processed.map(l => l.te_z),
        line: { color: "#111827", width: 1 },
        hoverinfo: "skip",
      },
      {
        type: "scatter", mode: "markers", name: "LE",
        x: processed.map(l => l.le_r), y: processed.map(l => l.le_z),
        text: processed.map(l => l.name),
        marker: { symbol: "circle", size: 7, color: "white", line: { color: processed.map(l => colorByName[l.name] || "#333"), width: 2 } },
        hovertemplate: "%{text}<br>r=%{x:.2f}  z=%{y:.2f}<extra>LE</extra>",
      },
      {
        type: "scatter", mode: "markers", name: "TE",
        x: processed.map(l => l.te_r), y: processed.map(l => l.te_z),
        text: processed.map(l => l.name),
        marker: { symbol: "square", size: 7, color: "white", line: { color: processed.map(l => colorByName[l.name] || "#333"), width: 2 } },
        hovertemplate: "%{text}<br>r=%{x:.2f}  z=%{y:.2f}<extra>TE</extra>",
      },
    ] : []),
    ...(data?.cutoff_line ? [{
      type: "scatter", mode: "lines+markers", name: "Cut-off",
      x: data.cutoff_line.r, y: data.cutoff_line.z,
      line: { color: "#111827", width: 2, dash: "dash" },
      marker: { color: "#111827", size: 6 },
    }] : []),
  ];

  // ── Wrap traces ────────────────────────────────────────────────────────────
  const wrapTraces = [
    ...(data?.wrap || []).map((l) => ({
      type: "scatter", mode: "lines", name: l.name,
      x: l.theta_deg.map((t, j) => l.r[j] * Math.cos(t * DEG2RAD)),
      y: l.theta_deg.map((t, j) => l.r[j] * Math.sin(t * DEG2RAD)),
      legendgroup: l.name,
      line: { color: colorByName[l.name] || LINE_COLORS[0], width: 1.8, shape: "spline" },
    })),
    ...(data?.thickness_lines || []).flatMap((l, i) => {
      const color = colorByName[l.name] || LINE_COLORS[i % LINE_COLORS.length];
      return [
        { type: "scatter", mode: "lines", name: `${l.name} PS`, x: l.ps_x, y: l.ps_y, legendgroup: l.name, showlegend: false, line: { color, width: 0.8, shape: "spline" } },
        { type: "scatter", mode: "lines", name: `${l.name} SS`, x: l.ss_x, y: l.ss_y, legendgroup: l.name, showlegend: false, line: { color, width: 0.8, shape: "spline" } },
      ];
    }),
  ];

  // ── Conformal mapping traces ───────────────────────────────────────────────
  const confTraces = (data?.conf_map || []).map((l) => ({
    type: "scatter", mode: "lines", name: l.name,
    x: l.m, y: l.s,
    line: { color: colorByName[l.name] || LINE_COLORS[0], width: 1.8, shape: "spline" },
  }));

  // ── Cone area traces ───────────────────────────────────────────────────────
  const coneTraces = data?.cone_area ? [{
    type: "scatter", mode: "lines", name: "Hub",
    x: data.cone_area.m, y: data.cone_area.area,
    line: { color: "#111827", width: 1.5, shape: "spline" },
  }] : [];

  // ── 3D traces ─────────────────────────────────────────────────────────────
  const nBlades = activeDesign?.n_blades || 6;
  const traces3d = [];
  if (data?.surface_3d) {
    const { blade, hub, shroud } = data.surface_3d;
    if (blade)             traces3d.push(bladeMesh(blade,                nBlades, COLOR_DARK,  "Blade",   0.90));
    if (hub)               traces3d.push(revolutionMesh(hub.x,    hub.z,           COLOR_DARK,  "Hub",     0.35));
    if (shroud)            traces3d.push(revolutionMesh(shroud.x, shroud.z,         COLOR_LIGHT, "Shroud",  0.18));
    if (data.cutoff_line)  traces3d.push(revolutionMesh(data.cutoff_line.r, data.cutoff_line.z, "#374151", "Cut-off", 0.15));
  }

  // ── Curvature + skeleton traces ────────────────────────────────────────────
  const hasSurface = curvData != null && Array.isArray(curvData.surface_x) && curvData.surface_x.length > 0;
  let curvTraces = [], skelTraces = [], zRange = [-1e-4, 1e-4];
  if (curvData) {
    const flat   = curvData.kappa_v.flat(1);
    const maxAbs = flat.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
    zRange = maxAbs < 1e-12 ? [-1e-4, 1e-4] : [-maxAbs, maxAbs];

    const { curvX, curvY, curvZ, skelX, skelY, skelZ } = computeInflection(curvData, hasSurface);
    const hasInfl = curvX.length > 0;

    curvTraces = [
      {
        type: "surface", x: curvData.u, y: curvData.v, z: curvData.kappa_v,
        surfacecolor: curvData.kappa_v,
        colorscale: [[0, COLOR_DARK], [0.5, "#ffffff"], [1, COLOR_PRIMARY]],
        reversescale: false,
        cmin: zRange[0], cmax: zRange[1],
        colorbar: { title: { text: "κᵥ [mm⁻¹]", side: "right", font: { size: 10 } },
                    thickness: 12, len: 0.55, tickfont: { size: 9 } },
        hovertemplate: "u: %{x:.4f}<br>v: %{y:.4f}<br>κᵥ: %{z:.5g}<extra></extra>",
        opacity: 0.92, lighting: { ambient: 0.8, diffuse: 0.6, specular: 0.1 },
        showlegend: false,
      },
      hasInfl && inflectionMarker(curvX, curvY, curvZ),
    ].filter(Boolean);

    skelTraces = hasSurface ? [
      {
        type: "surface",
        x: curvData.surface_x, y: curvData.surface_y, z: curvData.surface_z,
        surfacecolor: curvData.surface_z,
        colorscale: [[0, COLOR_LIGHT], [1, COLOR_DARK]],
        showscale: false, opacity: 0.88,
        lighting: { ambient: 0.75, diffuse: 0.8, specular: 0.25 },
        hovertemplate: "x: %{x:.2f}  y: %{y:.2f}  z: %{z:.2f}<extra></extra>",
        showlegend: false,
      },
      hasInfl && inflectionMarker(skelX, skelY, skelZ),
    ].filter(Boolean) : [];
  }

  // ── Shared config ──────────────────────────────────────────────────────────
  const commonConfig = {
    displayModeBar: "hover",
    modeBarButtons: [["zoom2d", "pan2d", "autoScale2d", "resetScale2d", "toImage"]],
    scrollZoom: true,
    responsive: true,
    displaylogo: false,
  };
  const config3d = {
    displayModeBar: "hover",
    modeBarButtons: [["zoom3d", "pan3d", "orbitRotation", "resetCameraDefault3d", "toImage"]],
    scrollZoom: true, responsive: true, displaylogo: false,
  };
  const commonLayout = {
    paper_bgcolor: "transparent",
    plot_bgcolor: "#f9fafb",
    showlegend: true,
    legend: { font: { size: 9 }, bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1, x: 0, xanchor: "left", y: 1, yanchor: "top" },
    hovermode: "closest",
    dragmode: "pan",
  };
  const tightMargin = { l: 50, r: 14, t: 10, b: 40 };
  const axisBase = {
    gridcolor: "#e5e7eb",
    linecolor: "#d1d5db",
    tickcolor: "#9ca3af",
    tickfont: { size: 10 },
    zeroline: false,
    showspikes: true,
    spikemode: "across",
    spikecolor: "#9ca3af",
    spikethickness: 1,
    spikedash: "dot",
  };

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      {/* Panel header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Preview</span>
        {(loading || curvLoading) && <span className="text-xs text-bh-500 ml-1">Updating…</span>}
        <div className="flex rounded border border-gray-200 overflow-hidden ml-auto">
          {MODES.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setMode(key)}
              className={`px-2 py-0.5 text-xs font-medium transition-colors ${
                mode === key ? "bg-bh-600 text-white" : "bg-white text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart area */}
      <div className="flex-1 min-h-0">
        {!data && !loading && mode !== "curv" && mode !== "skel" && (
          <div className="h-full flex items-center justify-center text-xs text-gray-400">
            No geometry data yet
          </div>
        )}

        {data && mode === "hs" && (
          <Plot
            data={hsTraces}
            layout={{ ...commonLayout, margin: tightMargin,
              xaxis: { ...axisBase, title: { text: "r [mm]", font: { size: 11 } }, ...savedRange("hs").x },
              yaxis: { ...axisBase, title: { text: "z [mm]", font: { size: 11 } }, scaleanchor: "x", scaleratio: 1, ...savedRange("hs").y } }}
            onRelayout={onRelayout2D("hs")}
            config={commonConfig}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
          />
        )}

        {data && mode === "wrap" && (
          <Plot
            data={wrapTraces}
            layout={{ ...commonLayout, margin: tightMargin,
              xaxis: { ...axisBase, title: { text: "x [mm]", font: { size: 11 } }, ...savedRange("wrap").x },
              yaxis: { ...axisBase, title: { text: "y [mm]", font: { size: 11 } }, scaleanchor: "x", scaleratio: 1, ...savedRange("wrap").y } }}
            onRelayout={onRelayout2D("wrap")}
            config={commonConfig}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
          />
        )}

        {data && mode === "conf" && (
          <Plot
            data={confTraces}
            layout={{ ...commonLayout, margin: tightMargin,
              xaxis: { ...axisBase, title: { text: "m [mm]", font: { size: 11 } }, ...savedRange("conf").x },
              yaxis: { ...axisBase, title: { text: "∫r dθ [mm]", font: { size: 11 } }, scaleanchor: "x", scaleratio: 1, ...savedRange("conf").y } }}
            onRelayout={onRelayout2D("conf")}
            config={commonConfig}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
          />
        )}

        {data && mode === "cone" && (
          <Plot
            data={coneTraces}
            layout={{ ...commonLayout, margin: tightMargin,
              xaxis: { ...axisBase, title: { text: "m [mm]", font: { size: 11 } }, ...savedRange("cone").x },
              yaxis: { ...axisBase, title: { text: "Cone area [mm²]", font: { size: 11 } }, ...savedRange("cone").y } }}
            onRelayout={onRelayout2D("cone")}
            config={commonConfig}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
          />
        )}

        {data && mode === "porc" && (
          <Plot
            data={buildPorcupineTraces(processed, colorByName)}
            layout={{ ...commonLayout, margin: tightMargin,
              xaxis: { ...axisBase, title: { text: "r [mm]", font: { size: 11 } }, ...savedRange("porc").x },
              yaxis: { ...axisBase, title: { text: "z [mm]", font: { size: 11 } }, scaleanchor: "x", scaleratio: 1, ...savedRange("porc").y } }}
            onRelayout={onRelayout2D("porc")}
            config={commonConfig}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
          />
        )}

        {mode === "3d" && !data?.surface_3d && (
          <div className="h-full flex items-center justify-center text-xs text-gray-400 text-center px-4">
            Complete MECO and Beta tabs first
          </div>
        )}

        {mode === "3d" && data?.surface_3d && (
          <Plot
            data={traces3d}
            layout={{
              margin: { l: 0, r: 0, t: 0, b: 0 },
              scene: {
                xaxis: { title: "x [mm]", gridcolor: "#e5e7eb" },
                yaxis: { title: "y [mm]", gridcolor: "#e5e7eb" },
                zaxis: { title: "z [mm]", gridcolor: "#e5e7eb" },
                aspectmode: "data",
                camera: cameraRef.current,
              },
              paper_bgcolor: "transparent",
              showlegend: true,
              legend: { font: { size: 9 }, bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1, x: 0, xanchor: "left", y: 1, yanchor: "top" },
            }}
            onRelayout={(e) => { if (e["scene.camera"]) cameraRef.current = e["scene.camera"]; }}
            config={config3d}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
          />
        )}

        {(mode === "curv" || mode === "skel") && curvLoading && (
          <div className="h-full flex items-center justify-center text-xs text-gray-400">
            Computing curvature…
          </div>
        )}

        {mode === "curv" && curvData && (
          <Plot
            data={curvTraces}
            layout={{
              margin: { l: 0, r: 0, t: 0, b: 0 },
              paper_bgcolor: "transparent",
              scene: {
                xaxis: { title: { text: "u", font: { size: 9 } }, gridcolor: "#e5e7eb" },
                yaxis: { title: { text: "v", font: { size: 9 } }, gridcolor: "#e5e7eb" },
                zaxis: { title: { text: "κᵥ [mm⁻¹]", font: { size: 9 } }, gridcolor: "#e5e7eb", range: zRange },
                aspectmode: "manual", aspectratio: { x: 2, y: 1, z: 0.8 },
                camera: curvCamRef.current,
              },
              showlegend: false,
            }}
            onRelayout={(e) => { if (e["scene.camera"]) curvCamRef.current = e["scene.camera"]; }}
            config={config3d}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
          />
        )}

        {mode === "skel" && curvData && !hasSurface && (
          <div className="h-full flex items-center justify-center text-xs text-gray-400 text-center px-4">
            Skeleton surface not available
          </div>
        )}

        {mode === "skel" && curvData && hasSurface && (
          <Plot
            data={skelTraces}
            layout={{
              margin: { l: 0, r: 0, t: 0, b: 0 },
              paper_bgcolor: "transparent",
              scene: {
                xaxis: { title: { text: "x [mm]", font: { size: 9 } }, gridcolor: "#e5e7eb" },
                yaxis: { title: { text: "y [mm]", font: { size: 9 } }, gridcolor: "#e5e7eb" },
                zaxis: { title: { text: "z [mm]", font: { size: 9 } }, gridcolor: "#e5e7eb" },
                aspectmode: "data",
                camera: skelCamRef.current,
              },
              showlegend: false,
            }}
            onRelayout={(e) => { if (e["scene.camera"]) skelCamRef.current = e["scene.camera"]; }}
            config={config3d}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
          />
        )}
      </div>
    </div>
  );
}
